import React from "react";
import { Card, Button, Badge } from "./ui";
import { DashboardGrid } from "./layout";
import { PLAN_KEYS, PLAN_CONFIG } from "../shared/plan";

function getPlanTone(planKey, currentPlan) {
  if (planKey === currentPlan) return "success";
  if (planKey === PLAN_KEYS.PRO) return "review";
  if (planKey === PLAN_KEYS.ENTERPRISE) return "published";
  return "default";
}

function getNumericPrice(price) {
  return Number(String(price || "0").replace(/[^\d.]/g, "")) || 0;
}

function getDisplayPrice(plan, billingCycle) {
  const monthlyPrice = getNumericPrice(plan.monthlyPrice);

  if (billingCycle === "annual") {
    return `€${Math.round(monthlyPrice * 0.8)}/mo`;
  }

  return plan.monthlyPrice || `€${monthlyPrice}/mo`;
}

function getAnnualNote(plan) {
  return `Billed annually · ${plan.annualSavings || "Save 20%"} · ${
    plan.annualPrice || "Annual price"
  }/year`;
}

function getPlanRank(planKey) {
  if (planKey === PLAN_KEYS.STARTER) return 1;
  if (planKey === PLAN_KEYS.PRO) return 2;
  if (planKey === PLAN_KEYS.ENTERPRISE) return 3;
  return 1;
}

export default function SubscriptionPage({
  currentPlan = PLAN_KEYS.STARTER,
  selectedBillingCycle = "monthly",
  userBillingCycle = "monthly",
  hasStripeSubscription = false,

  monthlyCredits = 0,
  creditsRemaining = 0,
  reportsGeneratedThisMonth = 0,
  maxReports = 0,

  onBillingCycleChange,
  onUpgrade,
  onChangeSubscriptionPlan,
  onManageBilling
}) {
  const [billingCycle, setBillingCycle] = React.useState(
    selectedBillingCycle || "monthly"
  );

  const updateBillingCycle = (cycle) => {
    setBillingCycle(cycle);

    if (onBillingCycleChange) {
      onBillingCycleChange(cycle);
    }
  };

  const normalizedCurrentPlan = currentPlan || PLAN_KEYS.STARTER;
  const currentPlanRank = getPlanRank(normalizedCurrentPlan);

  const plans = [
    PLAN_CONFIG[PLAN_KEYS.STARTER],
    PLAN_CONFIG[PLAN_KEYS.PRO],
    PLAN_CONFIG[PLAN_KEYS.ENTERPRISE]
  ].filter(Boolean);

  const handlePlanAction = (planKey) => {
    const targetRank = getPlanRank(planKey);

    if (!hasStripeSubscription) {
      onUpgrade(planKey, billingCycle);
      return;
    }

    if (targetRank > currentPlanRank) {
      onUpgrade(planKey, billingCycle);
      return;
    }

    if (targetRank < currentPlanRank) {
      onChangeSubscriptionPlan(planKey, billingCycle);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          marginBottom: "28px",
          flexWrap: "wrap"
        }}
      >
        <Button
          variant={billingCycle === "monthly" ? "primary" : "secondary"}
          onClick={() => updateBillingCycle("monthly")}
        >
          Monthly
        </Button>

        <Button
          variant={billingCycle === "annual" ? "primary" : "secondary"}
          onClick={() => updateBillingCycle("annual")}
        >
          Annual
        </Button>

        <Badge tone="success">Save 20%</Badge>
      </div>

      <DashboardGrid min="300px">
        {plans.map((plan) => {
          const isCurrent = normalizedCurrentPlan === plan.key;
          const isEnterprise = plan.key === PLAN_KEYS.ENTERPRISE;
          const displayPrice = getDisplayPrice(plan, billingCycle);
          const targetRank = getPlanRank(plan.key);
          const isDowngrade = targetRank < currentPlanRank;

          const features = Array.isArray(plan.marketingFeatures)
            ? plan.marketingFeatures
            : Array.isArray(plan.features)
            ? plan.features
            : [];

          const creditLimits = [
            ["Monthly credits", plan.monthlyCredits],
            ["Max reports / month", plan.maxReports]
          ];

          return (
            <Card
              key={plan.key}
              style={{
                border: "2px solid #047857",
                position: "relative",
                overflow: "visible",
                borderRadius: "24px",
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                background: "#ffffff",
                boxShadow:
                  plan.highlighted || isCurrent || isEnterprise
                    ? "0 14px 36px rgba(4,120,87,0.08)"
                    : "0 10px 28px rgba(15,23,42,0.05)"
              }}
            >
              {plan.highlighted && (
                <div
                  style={{
                    position: "absolute",
                    top: "0",
                    right: "24px",
                    transform: "translateY(-50%)",
                    background: "#0f172a",
                    color: "#ffffff",
                    padding: "8px 16px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: "800",
                    zIndex: 20,
                    whiteSpace: "nowrap"
                  }}
                >
                  Most Popular
                </div>
              )}

              <div style={{ marginBottom: "18px" }}>
                <Badge tone={getPlanTone(plan.key, normalizedCurrentPlan)}>
                  {isCurrent && hasStripeSubscription
                    ? "Current Plan"
                    : plan.name}
                </Badge>
              </div>

              <div
                style={{
                  fontSize: "42px",
                  fontWeight: "900",
                  color: "#0f172a"
                }}
              >
                {displayPrice}
              </div>

              {billingCycle === "annual" && (
                <div
                  style={{
                    marginTop: "10px",
                    color: "#047857",
                    fontSize: "13px",
                    fontWeight: "700"
                  }}
                >
                  {getAnnualNote(plan)}
                </div>
              )}

              <div
                style={{
                  marginTop: "18px",
                  color: "#6b7280",
                  lineHeight: 1.65,
                  fontSize: "14px",
                  minHeight: "72px"
                }}
              >
                {plan.description || plan.bestFor}
              </div>

              <div
                style={{
                  marginTop: "18px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                  padding: "14px",
                  background: "#f9fafb",
                  display: "grid",
                  gap: "10px"
                }}
              >
                {creditLimits.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      fontSize: "13px",
                      color: "#4b5563"
                    }}
                  >
                    <span>{label}</span>
                    <strong style={{ color: "#111827" }}>{value}</strong>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: "10px",
                  marginTop: "22px",
                  alignContent: "start"
                }}
              >
                {features.map((feature) => (
                  <div
                    key={feature}
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      color: "#374151",
                      fontSize: "14px",
                      lineHeight: 1.45
                    }}
                  >
                    <span style={{ color: "#047857", fontWeight: "900" }}>
                      ✓
                    </span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: "auto", paddingTop: "26px" }}>
                {isCurrent ? (
                  !hasStripeSubscription ? (
                    <Button
                      variant="primary"
                      onClick={() => onUpgrade(plan.key, billingCycle)}
                      style={{ width: "100%", height: "50px", fontWeight: "700" }}
                    >
                      Subscribe to {plan.name}
                    </Button>
                  ) : billingCycle !== userBillingCycle ? (
                    <Button
                      variant="primary"
                      onClick={() =>
                        onChangeSubscriptionPlan(plan.key, billingCycle)
                      }
                      style={{ width: "100%", height: "50px", fontWeight: "700" }}
                    >
                      Switch to {billingCycle === "annual" ? "Annual" : "Monthly"}
                    </Button>
                  ) : (
                    <Button disabled style={{ width: "100%", height: "50px" }}>
                      Active Plan
                    </Button>
                  )
                ) : (
                  <Button
                    variant={
                      isDowngrade
                        ? "secondary"
                        : plan.key === PLAN_KEYS.PRO
                        ? "primary"
                        : "warning"
                    }
                    onClick={() => handlePlanAction(plan.key)}
                    style={{ width: "100%", height: "50px", fontWeight: "700" }}
                  >
                    {isDowngrade
                      ? `Downgrade to ${plan.name}`
                      : !hasStripeSubscription
                      ? `Subscribe to ${plan.name}`
                      : plan.cta || `Choose ${plan.name}`}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </DashboardGrid>

      {hasStripeSubscription && normalizedCurrentPlan !== PLAN_KEYS.STARTER && (
        <Card
          style={{
            marginTop: "28px",
            border: "1px solid #d1d5db",
            borderRadius: "20px"
          }}
        >
          <h3
            style={{
              marginTop: 0,
              fontSize: "22px",
              fontWeight: "800",
              color: "#111827"
            }}
          >
            Manage Subscription
          </h3>

          <p style={{ color: "#6b7280", lineHeight: 1.6 }}>
            Manage billing, payment methods, invoices, and subscription access.
          </p>

          <div
            style={{
              marginTop: "16px",
              padding: "14px",
              borderRadius: "14px",
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              display: "grid",
              gap: "8px",
              color: "#111827",
              fontSize: "14px",
              fontWeight: "700"
            }}
          >
            <div>
              Credits: {creditsRemaining}/{monthlyCredits}
            </div>

            <div>
              Reports: {reportsGeneratedThisMonth}/{maxReports}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "18px"
            }}
          >
            <Button variant="primary" onClick={onManageBilling}>
              Manage Billing
            </Button>

            {normalizedCurrentPlan === PLAN_KEYS.ENTERPRISE && (
              <>
                <Button
                  variant="secondary"
                  onClick={() =>
                    onChangeSubscriptionPlan(PLAN_KEYS.PRO, billingCycle)
                  }
                >
                  Downgrade to Pro
                </Button>

                <Button
                  variant="secondary"
                  onClick={() =>
                    onChangeSubscriptionPlan(PLAN_KEYS.STARTER, billingCycle)
                  }
                >
                  Downgrade to Starter
                </Button>
              </>
            )}

            {normalizedCurrentPlan === PLAN_KEYS.PRO && (
              <Button
                variant="secondary"
                onClick={() =>
                  onChangeSubscriptionPlan(PLAN_KEYS.STARTER, billingCycle)
                }
              >
                Downgrade to Starter
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}